import {
  Input,
  InputType,
  Label,
  Toggle,
  Select,
  Typography,
} from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { FormError } from '../../components/ui/FormError';
import { useSettingsHook } from '../../hooks/settingsStorageHook';
import { testId } from '../../utils';
import { NAME_MAX } from '../../utils/constants';
import { ChangePassword } from './ChangePassword';
import { FormElement } from './FormElement';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Form from '@radix-ui/react-form';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

export const SettingsAccount: React.FC = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'route.user.settings.account',
  });

  const SettingSchema = z.object({
    email: z.string().email().optional(),
    firstName: z
      .string()
      .max(NAME_MAX, t('input.first_name.max', { count: NAME_MAX })!)
      .optional(),
    lastName: z
      .string()
      .max(NAME_MAX, t('input.last_name.max', { count: NAME_MAX })!)
      .optional(),
    preferredCurrency: z.nativeEnum(Cornerstone.SupportedCurrency).optional(),
    languageCode: z.nativeEnum(Cornerstone.SupportedLanguage).optional(),
    wantsNewsletter: z.boolean().optional(),
  });

  type SettingSchema = z.infer<typeof SettingSchema>;
  const { handleInvalidate } = useSettingsHook();
  const { data, isLoading, isFetching, isError, refetch } = Cornerstone.useMe();

  const me = data?.data;

  const {
    handleSubmit,
    register,
    control,
    watch,
    reset,
    setValue,
    formState,
    setError,
    clearErrors,
  } = useForm<SettingSchema>({
    resolver: zodResolver(SettingSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      preferredCurrency: undefined,
      languageCode: undefined,
      wantsNewsletter: false,
    },
    values: {
      email: me?.email,
      firstName: me?.firstName || undefined,
      lastName: me?.lastName || undefined,
      preferredCurrency: me?.preferredCurrency || undefined,
      languageCode: me?.languageCode || undefined,
      wantsNewsletter: me?.wantsNewsletter || false,
    },
  });

  const { isSuccess: editUserSuccess, mutate: editUserMutate } =
    Cornerstone.useEditMyUser({
      mutation: {
        onSuccess: () => {
          refetch();
          handleInvalidate();
        },
        onError: (error: any) => {
          console.log({
            title: 'Failed to update account',
            description: error?.response?.data?.message || error.message,
            type: 'error',
          });
          setError('root.serverError', {
            message: t('failed_update') || '',
          });
        },
      },
    });

  const onSubmit = React.useCallback(
    (data: SettingSchema) => {
      const userData = {
        firstName: data.firstName,
        lastName: data.lastName,
        languageCode: data.languageCode,
        preferredCurrency: data.preferredCurrency,
        wantsNewsletter: data.wantsNewsletter,
      };
      const filteredData = Object.fromEntries(
        Object.entries(userData).filter(
          ([key, value]) => value !== me?.[key as keyof typeof me],
        ),
      );

      editUserMutate({
        data: filteredData,
      });
    },
    [editUserMutate, me],
  );

  const resetForm = React.useCallback(() => {
    reset(me);
  }, [reset, me]);

  const onToggleNewsletter = React.useCallback(
    (checked: boolean) => {
      setValue('wantsNewsletter', checked);
      handleSubmit(onSubmit)();
    },
    [handleSubmit, onSubmit, setValue],
  );

  const showLoader = isLoading || isFetching || isError || !me;

  const names = watch(['firstName', 'lastName']);
  const languageCode = watch('languageCode');
  const wantsNewsletter = watch('wantsNewsletter');

  const firstNameError = formState.errors.firstName;
  const lastNameError = formState.errors.lastName;

  const fullName = names.join(' ').trim();
  const newsLetterLabel = wantsNewsletter ? t('subscribed') : t('unsubscribed');

  function languageLabel(code: string | undefined) {
    switch (code) {
      case Cornerstone.SupportedLanguage.FR:
        return t('french');
      case Cornerstone.SupportedLanguage.DE:
        return t('german');
      case Cornerstone.SupportedLanguage.NL:
        return t('dutch');
      case Cornerstone.SupportedLanguage.PT:
        return t('portuguese');
      case Cornerstone.SupportedLanguage.IT:
        return t('italian');
      case Cornerstone.SupportedLanguage.ES:
        return t('spanish');
      default:
        return t('english');
    }
  }
  return (
    <>
      {!!formState.errors.root?.serverError && (
        <Alert variant="error" {...testId('error', 'message')}>
          {formState.errors.root?.serverError?.message}
        </Alert>
      )}

      <Form.Root
        noValidate
        onChange={() => {
          clearErrors('root.serverError');
        }}
      >
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <FormElement
              onCancel={resetForm}
              value={field.value}
              label={t('email')}
              title={t('modal_title_email')}
              loading={showLoader}
              {...testId('email')}
              success={editUserSuccess}
              // Functionality disabled until post-beta (BLT-871)
              // onSubmit={handleSubmit(onSubmit)}
              error={fieldState.error}
              serverError={formState.errors.root?.serverError?.message}
            >
              <div className="flex flex-col gap-2">
                <Label {...testId('current-email', 'label')}>
                  {t('current_email')}
                </Label>
                <Typography className="px-2" variant="body">
                  {me?.email}
                </Typography>
              </div>
              <Form.Field name={field.name}>
                <Form.Label>
                  <Label {...testId('new-email', 'label')}>
                    {t('new_email')}
                  </Label>
                </Form.Label>
                <Form.Control asChild>
                  <Input
                    {...testId('new-email')}
                    disabled
                    type={InputType.EMAIL}
                    {...field}
                  />
                </Form.Control>
                <Typography variant="footnote">
                  {t('feature_coming_soon')}
                </Typography>
                {fieldState.error && (
                  <FormError {...testId('email', 'error')}>
                    {fieldState.error?.message}
                  </FormError>
                )}
              </Form.Field>
            </FormElement>
          )}
        />

        <FormElement
          onCancel={resetForm}
          label={t('full_name')}
          title={t('modal_title_name')}
          value={fullName}
          loading={showLoader}
          success={editUserSuccess}
          onSubmit={handleSubmit(onSubmit)}
          error={firstNameError || lastNameError}
          data-testid="full-name"
          serverError={formState.errors.root?.serverError?.message}
        >
          <Form.Field name={'firstName'} className="space-y-1">
            <Form.Label>
              <Label {...testId('firstname', 'label')}>{t('first_name')}</Label>
            </Form.Label>
            <Form.Control asChild>
              <Input
                invalid={!!firstNameError}
                {...testId('firstname')}
                type={InputType.TEXT}
                {...register('firstName')}
              />
            </Form.Control>
            {firstNameError && (
              <FormError {...testId('firstname', 'error')}>
                {firstNameError?.message}
              </FormError>
            )}
          </Form.Field>

          <Form.Field name={'lastName'} className="space-y-1">
            <Form.Label>
              <Label {...testId('lastname', 'label')}>{t('last_name')}</Label>
            </Form.Label>
            <Form.Control asChild>
              <Input
                invalid={!!lastNameError}
                {...testId('lastname')}
                type={InputType.TEXT}
                {...register('lastName')}
              />
            </Form.Control>
            {lastNameError && (
              <FormError {...testId('lastname', 'error')}>
                {lastNameError?.message}
              </FormError>
            )}
          </Form.Field>
        </FormElement>

        <ChangePassword showLoader={showLoader} />

        <Controller
          name="preferredCurrency"
          control={control}
          render={({ field, fieldState }) => (
            <FormElement
              onCancel={resetForm}
              label={t('currency')}
              title={t('modal_title_currency')}
              value={field.value}
              loading={showLoader}
              success={editUserSuccess}
              onSubmit={handleSubmit(onSubmit)}
              {...testId('currency')}
              serverError={formState.errors.root?.serverError?.message}
            >
              <Form.Field name={field.name} className="space-y-1">
                <Form.Control asChild>
                  <Select
                    {...testId('currency')}
                    value={field.value}
                    onChange={field.onChange}
                    data={Object.values(Cornerstone.SupportedCurrency).map(
                      (value) => ({ value, disabled: false }),
                    )}
                  />
                </Form.Control>
                {fieldState.error && (
                  <FormError {...testId('currency', 'error')}>
                    {fieldState.error?.message}
                  </FormError>
                )}
              </Form.Field>
            </FormElement>
          )}
        />

        <Controller
          name="languageCode"
          control={control}
          render={({ field, fieldState }) => (
            <FormElement
              onCancel={resetForm}
              label={t('language')}
              title={t('modal_title_language')}
              value={languageLabel(field.value)}
              loading={showLoader}
              success={editUserSuccess}
              onSubmit={handleSubmit(onSubmit)}
              {...testId('language')}
              serverError={formState.errors.root?.serverError?.message}
            >
              <Form.Field name={field.name} className="space-y-1">
                <Form.Control asChild>
                  <Select
                    {...testId('language')}
                    value={field.value}
                    onChange={field.onChange}
                    data={Object.values(Cornerstone.SupportedLanguage).map(
                      (value) => ({
                        value,
                        label: languageLabel(value),
                        disabled: value !== 'EN',
                      }),
                    )}
                  />
                </Form.Control>
                {fieldState.error && (
                  <FormError {...testId('language', 'error')}>
                    {fieldState.error?.message}
                  </FormError>
                )}
              </Form.Field>
            </FormElement>
          )}
        />

        <Controller
          name="wantsNewsletter"
          control={control}
          render={({ field }) => (
            <FormElement
              label={t('wants_newsletter')}
              value={field.value}
              isToggle={true}
              loading={showLoader}
              {...testId('newsletter')}
            >
              <Form.Field name={field.name} className="flex items-center">
                <Form.Label>
                  <Typography
                    variant="body-dark"
                    {...testId('newsletter', 'label')}
                    className="pr-2"
                  >
                    {newsLetterLabel}
                  </Typography>
                </Form.Label>
                <Form.Control asChild>
                  <Toggle
                    {...testId('newsletter')}
                    checked={!!field.value}
                    onCheckedChange={onToggleNewsletter}
                  />
                </Form.Control>
              </Form.Field>
            </FormElement>
          )}
        />
      </Form.Root>
    </>
  );
};
